import { Instagram, Facebook, Linkedin, Music2, ExternalLink } from 'lucide-react';
import type { VisitorConfig, SitePageSummary } from '../../types';
import { REOPEN_COOKIE_CHOICE_EVENT } from './SiteWidgets';

// Pied de page unique, partagé par l'accueil (VisitorPortal) et toutes les
// pages créées avec l'éditeur (PageRenderer) — auparavant, seul l'accueil
// avait ce pied de page riche (réseaux, univers, navigation, newsletter) ;
// les autres pages retombaient sur une version générique à trois lignes.
//
// Les liens vers des ancres de l'accueil ("#presentation") sont préfixés
// d'un "/" ("/#presentation") : depuis une page qui n'est pas l'accueil, une
// ancre nue ne ferait qu'ajouter le fragment à l'URL courante sans y
// naviguer.

const isExternalLink = (value: string | undefined): value is string =>
  typeof value === 'string' && /^https?:\/\//i.test(value.trim());

interface SiteFooterProps {
  config: VisitorConfig;
  menuPages: SitePageSummary[];
}

export function SiteFooter({ config, menuPages }: SiteFooterProps) {
  const themeColor = config.brandColor || '#D95D39';
  const logoForDarkBackground = config.brandLogoDarkUrl || config.brandLogoUrl || '';
  const logoAlt = config.brandLogoAlt || config.brandLogoText || 'feed-it';
  const logoHeight =
    config.brandLogoHeightPx && config.brandLogoHeightPx > 0 ? config.brandLogoHeightPx : 40;
  const ctaLabel = config.ctaLabel?.trim() || 'Collaborons';
  const visibleUniverses = (config.universes || []).filter((u) => u.visible !== false);
  const legal = config.footerLegalLinks || {};
  const hasGoogleTag = Boolean(config.googleTagId?.trim());

  const socialEntries = ([
    { key: 'instagram', url: config.socialLinks?.instagram, label: 'Instagram', Icon: Instagram },
    { key: 'linkedin', url: config.socialLinks?.linkedin, label: 'LinkedIn', Icon: Linkedin },
    { key: 'facebook', url: config.socialLinks?.facebook, label: 'Facebook', Icon: Facebook },
    { key: 'tiktok', url: config.socialLinks?.tiktok, label: 'TikTok', Icon: Music2 },
  ] as const).filter((entry): entry is typeof entry & { url: string } => isExternalLink(entry.url));

  return (
    <footer className="bg-slate-950 text-white/60 pt-16 pb-8 border-t border-white/10">
      <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 md:grid-cols-4 gap-12 text-xs">
        <div className="space-y-4">
          {logoForDarkBackground ? (
            <img
              src={logoForDarkBackground}
              alt={logoAlt}
              referrerPolicy="no-referrer"
              style={{ height: `${logoHeight}px` }}
              className="w-auto max-w-[220px] object-contain object-left block"
            />
          ) : (
            <span className="font-black text-3xl tracking-tight text-white block">
              {config.brandLogoText || 'feed-it'}
            </span>
          )}
          {config.footerDescription && (
            <p className="leading-relaxed text-white/60 font-medium">{config.footerDescription}</p>
          )}
          <div className="flex items-center gap-3 pt-2">
            {socialEntries.map(({ key, url, label, Icon }) => (
              <a
                key={key}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
                title={label}
                className="text-white/60 hover:text-white transition-colors"
              >
                <Icon className="w-4 h-4" />
              </a>
            ))}
          </div>

          {isExternalLink(config.socialLinks?.production) && (
            <a
              href={config.socialLinks?.production}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-[11px] font-bold text-white/70 hover:text-white transition-colors"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              {config.socialLinks?.productionLabel || 'Notre atelier de production'}
            </a>
          )}
        </div>

        {visibleUniverses.length > 0 && (
          <div className="space-y-4">
            <h5 className="font-black text-white text-[11px] uppercase tracking-widest">Nos Univers</h5>
            <ul className="space-y-2.5 font-bold">
              {visibleUniverses.map((universe) => (
                <li key={universe.id}>
                  <a
                    href={universe.link?.trim() || '/#univers'}
                    className="hover:text-white transition-colors inline-flex items-center gap-2"
                  >
                    <span
                      className="w-1.5 h-1.5 rounded-full shrink-0"
                      style={{ backgroundColor: universe.color || themeColor }}
                    />
                    {universe.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="space-y-4">
          <h5 className="font-black text-white text-[11px] uppercase tracking-widest">Navigation</h5>
          <ul className="space-y-2.5 font-bold">
            <li><a href="/#presentation" className="hover:text-white transition-colors">Notre Philosophie</a></li>
            <li><a href="/#realisations" className="hover:text-white transition-colors">Galerie Réalisations</a></li>
            <li><a href="/#chiffres" className="hover:text-white transition-colors">Témoignages & Évaluation</a></li>
            <li><a href="/#devis" className="hover:text-white transition-colors">{ctaLabel}</a></li>
            <li><a href="/devenir-prestataire" className="hover:text-white transition-colors">Devenir prestataire</a></li>
            {(config.customMenuLinks || []).map((link) => (
              <li key={link.id}>
                <a href={link.target} className="hover:text-white transition-colors">
                  {link.label}
                </a>
              </li>
            ))}
            {menuPages.map((page) => (
              <li key={page.id}>
                <a href={`/${page.slug}`} className="hover:text-white transition-colors">
                  {page.title}
                </a>
              </li>
            ))}
            <li><a href="/blog" className="hover:text-white transition-colors">Blog</a></li>
          </ul>
        </div>

        <div className="space-y-4">
          <h5 className="font-black text-white text-[11px] uppercase tracking-widest">
            {config.footerNewsletterTitle || 'Inspirations par Mail'}
          </h5>
          <p className="leading-relaxed text-white/60 font-semibold">
            {config.footerNewsletterText ||
              'Recevez de temps en temps nos nouvelles créations, buffets géants et ateliers culinaires éco-engagés.'}
          </p>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              alert('Merci pour votre inscription à la newsletter !');
            }}
            className="flex items-center"
          >
            <input
              type="email"
              required
              placeholder="Votre adresse mail..."
              className="bg-white/10 border border-white/20 px-3 py-2 rounded-l text-xs text-white focus:outline-none w-full placeholder-white/30"
            />
            <button
              type="submit"
              className="bg-white text-slate-900 px-3 py-2 rounded-r font-extrabold hover:bg-slate-100 transition-colors uppercase text-[10px]"
              style={{ backgroundColor: themeColor, color: 'white' }}
            >
              Inscrire
            </button>
          </form>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 mt-16 pt-8 border-t border-white/5 flex flex-col sm:flex-row items-center justify-between gap-4 text-[10px] font-bold text-white/40">
        <p>
          {config.footerCopyrightText ||
            `© ${new Date().getFullYear()} ${config.brandLogoText || 'feed-it'}. ${config.contactAddress || ''}. Tous droits réservés.`}
        </p>
        {(legal.mentionsUrl || legal.cguUrl || legal.rgpdUrl || hasGoogleTag) && (
          <div className="flex flex-wrap justify-center gap-4 uppercase tracking-wider">
            {legal.mentionsUrl && (
              <a href={legal.mentionsUrl} className="hover:text-white transition-colors">
                Mentions Légales
              </a>
            )}
            {legal.mentionsUrl && (legal.cguUrl || legal.rgpdUrl) && <span className="text-white/10">|</span>}
            {legal.cguUrl && (
              <a href={legal.cguUrl} className="hover:text-white transition-colors">
                CGU
              </a>
            )}
            {legal.cguUrl && legal.rgpdUrl && <span className="text-white/10">|</span>}
            {legal.rgpdUrl && (
              <a href={legal.rgpdUrl} className="hover:text-white transition-colors">
                RGPD & Cookies
              </a>
            )}
            {hasGoogleTag && (
              <button
                type="button"
                onClick={() => window.dispatchEvent(new Event(REOPEN_COOKIE_CHOICE_EVENT))}
                className="uppercase tracking-wider hover:text-white transition-colors cursor-pointer"
              >
                Gérer les cookies
              </button>
            )}
          </div>
        )}
      </div>
    </footer>
  );
}
